import { Vector2 } from "../Math/Vector2";

/** The message code for mouse down events. */
export const MESSAGE_MOUSE_DOWN: string = "MOUSE_DOWN";

/** The message code for mouse up events. */
export const MESSAGE_MOUSE_UP: string = "MOUSE_UP";

/** Defines key codes for keyboard keys. */
export enum Keys {

    /** The left arrow key */
    LEFT = 37,

    /** The up arrow key */
    UP = 38,

    /** The right arrow key */
    RIGHT = 39,

    /** The down arrow key */
    DOWN = 40,

    SPACE = 32,

    W = 87,
    w = 119,
    S = 83,
    s = 115,
    A = 65,
    a = 97,
    D = 68,
    d = 100,   
}

/** Contains mouse state data to be used throughout the engine. */
export class MouseContext {

    /** Indicates if the left mouse button is down. Default: false. */
    public leftDown: boolean;

    /** Indicates if the right mouse button is down. Default: false. */
    public rightDown: boolean;

    /** The mouse position. */
    public position: Vector2;

    public constructor(leftDown: boolean = false, rightDown: boolean = false, position: Vector2) {
        this.leftDown = leftDown;
        this.rightDown = rightDown;
        this.position = position;
    }
}

/** Manages all input from devices such as the mouse and keyboard. */
export class InputManager {

    private static _keys: boolean[] = [];

    private static _previousMouseX: number;
    private static _previousMouseY: number;
    private static _mouseX: number;
    private static _mouseY: number;
    private static _leftDown: boolean = false;
    private static _rightDown: boolean = false;
    private static _resolutionScale: Vector2 = Vector2.one;

    private static _mouseContext: MouseContext = new MouseContext(false, false, Vector2.zero);
    private static _mouseContextPrevious: MouseContext = new MouseContext(false, false, Vector2.zero);

    /** Timestamp (ms) when the touch started. */
    private static _touchStartTime: number = 0;

    /** Duration threshold (ms) for a long touch to be treated as a left click. */
    private static readonly LONG_TOUCH_THRESHOLD_MS: number = 1000;


    /**
     * Initializes the input manager.
     * @param viewport The canvas element to attach input events to
     */
    public static Initialize(viewport: HTMLCanvasElement): void {
        for (let i = 0; i < 255; ++i) {
            InputManager._keys[i] = false;
        }


        window.addEventListener("keydown", InputManager.onKeyDown);
        window.addEventListener("keyup", InputManager.onKeyUp);

        viewport.addEventListener("mousemove", e => InputManager.onMouseMove(e));
        viewport.addEventListener("mousedown", e => InputManager.onMouseDown(e));
        viewport.addEventListener("mouseup", e => InputManager.onMouseUp(e));
        viewport.addEventListener("contextmenu", e => e.preventDefault());

        // Touch support
        viewport.addEventListener("touchstart", e => InputManager.onTouchStart(e), { passive: false });
        viewport.addEventListener("touchmove", e => InputManager.onTouchMove(e), { passive: false });
        viewport.addEventListener("touchend", e => InputManager.onTouchEnd(e), { passive: false });
    }

    public static Update(){
        InputManager._mouseContextPrevious = InputManager._mouseContext;
        InputManager._mouseContext = new MouseContext(InputManager._leftDown, InputManager._rightDown, InputManager.getMousePosition());
    }

    /**
     * Indicates if the provided key is currently down.
     * @param key The key to check.
     */
    public static isKeyDown(key: Keys): boolean {
        return InputManager._keys[key];
    }

    /** Gets the current mouse position. */
    public static getMousePosition(): Vector2 {
        return new Vector2(this._mouseX, this._mouseY);
    }

    public static isMouseLeftDown(): boolean {
        return InputManager._leftDown;
    }

    public static isMouseRightDown(): boolean {
        return InputManager._rightDown;
    }

    /** Gets the current mouse position. */
    public static getPreviousMousePosition(): Vector2 {
        return new Vector2(this._previousMouseX, this._previousMouseY);
    }

    public static isMouseLeftClick(): boolean {
        return InputManager._mouseContext.leftDown && !InputManager._mouseContextPrevious.leftDown;
    }

    public static isMouseRightClick(): boolean {
        return InputManager._mouseContext.rightDown && !InputManager._mouseContextPrevious.rightDown;
    }

    /**
     * Sets the resolution scale, which is a ratio of the window width and height
     * versus the viewport canvas width and height.
     * @param scale The scale to set.
     */
    public static setResolutionScale( scale: Vector2 ): void {
        InputManager._resolutionScale.copyFrom( scale );
    }

    private static onKeyDown(event: KeyboardEvent): boolean {
        InputManager._keys[event.keyCode] = true;
        return true;
    }

    private static onKeyUp(event: KeyboardEvent): boolean {
        InputManager._keys[event.keyCode] = false;
        return true;
    }

    private static onMouseMove(event: MouseEvent): void {
        InputManager._previousMouseX = InputManager._mouseX;
        InputManager._previousMouseY = InputManager._mouseY;

        const rect = (event.target as HTMLElement).getBoundingClientRect();
        InputManager._mouseX = (event.clientX - Math.round(rect.left)) * (1 / InputManager._resolutionScale.x);
        InputManager._mouseY = (event.clientY - Math.round(rect.top)) * (1 / InputManager._resolutionScale.y);
    }

    public static ConvertGlobalToLocalPoint(event: MouseEvent) : Vector2 {
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        const mouseX = (event.clientX - Math.round(rect.left)) * (1 / InputManager._resolutionScale.x);
        const mouseY = (event.clientY - Math.round(rect.top)) * (1 / InputManager._resolutionScale.y);
        return new Vector2(mouseX, mouseY);
    }

    private static onMouseDown(event: MouseEvent): void {
        if (event.button === 0) {
            this._leftDown = true;
        } else if (event.button === 2) {
            this._rightDown = true;
        }

        //Message.send( MESSAGE_MOUSE_DOWN, this, new MouseContext( InputManager._leftDown, InputManager._rightDown, InputManager.getMousePosition() ) );
    }

    private static onMouseUp(event: MouseEvent): void {
        if (event.button === 0) {
            this._leftDown = false;
        } else if (event.button === 2) {
            this._rightDown = false;
        }
    }

    private static getTouchPosition(event: TouchEvent): Vector2 {
        const touch = event.touches[0] ?? event.changedTouches[0];
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        const x = (touch.clientX - Math.round(rect.left)) * (1 / InputManager._resolutionScale.x);
        const y = (touch.clientY - Math.round(rect.top)) * (1 / InputManager._resolutionScale.y);
        return new Vector2(x, y);
    }

    private static onTouchStart(event: TouchEvent): void {
        event.preventDefault();
        const pos = InputManager.getTouchPosition(event);
        InputManager._previousMouseX = InputManager._mouseX;
        InputManager._previousMouseY = InputManager._mouseY;
        InputManager._mouseX = pos.x;
        InputManager._mouseY = pos.y;
        InputManager._touchStartTime = performance.now();
    }

    private static onTouchMove(event: TouchEvent): void {
        event.preventDefault();
        const pos = InputManager.getTouchPosition(event);
        InputManager._previousMouseX = InputManager._mouseX;
        InputManager._previousMouseY = InputManager._mouseY;
        InputManager._mouseX = pos.x;
        InputManager._mouseY = pos.y;
    }

    private static onTouchEnd(event: TouchEvent): void {
        event.preventDefault();
        const duration = performance.now() - InputManager._touchStartTime;
        if (duration >= InputManager.LONG_TOUCH_THRESHOLD_MS) {
            // Long touch (>= 1s) → left click
            InputManager._leftDown = true;
            InputManager.Update();
            InputManager._leftDown = false;
        } else {
            // Short touch → right click
            InputManager._rightDown = true;
            InputManager.Update();
            InputManager._rightDown = false;
        }
    }
}